import I18n from 'i18n-js';
import React from 'react';

import { RCButton, RCTextWithIcon } from '@reverbdotcom/commons/src/rc';
import { parseAmount } from '@reverbdotcom/commons/src/money';
import TrashSvg from '@reverbdotcom/commons/design-system/icons/core/trash.svg?component';
import PencilSvg from '@reverbdotcom/commons/design-system/icons/core/pencil.svg?component';
import PlusCircle from '@reverbdotcom/commons/design-system/icons/core/plus-circle.svg?component';
import { RegionalRateType, IRegionalRate } from './edit_shipping_rate_card_regional_prices';

export interface IRegionalRateGroup {
  regionalRates: IRegionalRate[];
  rateType: RegionalRateType;
  countryCode: string;
  handleModalOpen: () => void;
  handleEditRateGroup: (RegionalRateType, IRegionalRate) => void;
  handleDeleteRateGroup: (RegionalRateType, IRegionalRate) => void;
  isFree?: boolean;
  allowCreateButton?: boolean;
}

export interface IRegionalRateGroupActive {
  regionalRate: IRegionalRate;
  rateType: RegionalRateType;
  countryCode: string;
  handleModalOpen: () => void;
  handleEditRateGroup: (RegionalRateType, IRegionalRate) => void;
  handleDeleteRateGroup: (RegionalRateType, IRegionalRate) => void;
  freeOrPaid: 'free' | 'paid';
  showCreateButton?: boolean;
}

function HiddenInputFieldsForRates({
  regionalRate,
  rateType,
}: {
  regionalRate: IRegionalRate;
  rateType: RegionalRateType;
}) {
  if (!regionalRate) { return null; }

  const inputName = rateType === RegionalRateType.EXPEDITED ? 'regional_expedited_rates' : 'regional_rates';

  return (
    <>
      <input
        type="hidden"
        value={regionalRate.areas.join(',')}
        name={`shipping_profile[shipping][rates][][${inputName}][][areas]`}
      />
      <input
        type="hidden"
        value={regionalRate.rate.amount}
        name={`shipping_profile[shipping][rates][][${inputName}][][rate][amount]`}
      />
    </>
  );
}

function RegionalRateGroupActive({
  regionalRate,
  rateType,
  countryCode,
  handleModalOpen,
  handleEditRateGroup,
  handleDeleteRateGroup,
  freeOrPaid,
  showCreateButton = true,
}: IRegionalRateGroupActive) {
  return (
    <>
      <div className="shipping-rate-card__regional__rate-group__body">
        <div className="shipping-rate-card__regional__rate-group__label">
          <span>
            {I18n.t(`discovery.dashboard.selling.shippingRates.shippingModeRadios.regional.options.${freeOrPaid}.selected`, {
              rateType: I18n.t(`discovery.dashboard.selling.shippingRates.shippingModeRadios.regional.options.rateType.${rateType}`),
              price: freeOrPaid === 'paid' && (regionalRate.rate.display || parseAmount(regionalRate.rate.amount, regionalRate.rate.currency).display),
            })}
          </span>
          <div>
            {`${regionalRate.areas.map((areaCode) => I18n.t(`discovery.regionalShippingAreas.${countryCode}.${areaCode}`)).join(', ')}`}
          </div>
        </div>
        <div className="shipping-rate-card__regional__rate-group__button-group">
          <RCButton
            variant="tertiary"
            onClick={() => handleEditRateGroup(rateType, regionalRate)}
            id={`edit-${rateType}-${regionalRate.rate.amountCents}-regional-rate-group-button`}
          >
            <RCTextWithIcon svgComponent={PencilSvg} placement="left">
              {I18n.t('discovery.dashboard.selling.shippingRates.shippingModeRadios.regional.rateGroup.edit')}
            </RCTextWithIcon>
          </RCButton>
          <RCButton
            variant="tertiary"
            onClick={() => handleDeleteRateGroup(rateType, regionalRate)}
            id={`delete-${rateType}-${regionalRate.rate.amountCents}-regional-rate-group-button`}
          >
            <RCTextWithIcon svgComponent={TrashSvg} placement="left">
              {I18n.t('discovery.dashboard.selling.shippingRates.shippingModeRadios.regional.rateGroup.delete')}
            </RCTextWithIcon>
          </RCButton>
          <HiddenInputFieldsForRates regionalRate={regionalRate} rateType={rateType} />
        </div>
      </div>
      {freeOrPaid === 'paid' &&
        <hr className="shipping-rate-card__regional__separator" />
      }
      {showCreateButton && (
        <div className="shipping-rate-card__regional__rate-group__footer">
          <RCButton
            onClick={handleModalOpen}
            id={`add-${rateType}-regional-rate-group-button`}
          >
            <RCTextWithIcon svgComponent={PlusCircle} placement="left">
              {I18n.t('discovery.dashboard.selling.shippingRates.shippingModeRadios.regional.rateGroup.add')}
            </RCTextWithIcon>
          </RCButton>
        </div>
      )}
    </>
  );
}

function RegionalRateGroup({
  regionalRates,
  rateType,
  countryCode,
  handleModalOpen,
  handleEditRateGroup,
  handleDeleteRateGroup,
  isFree = false,
  allowCreateButton = true,
}: IRegionalRateGroup) {
  const freeOrPaid = isFree ? 'free' : 'paid';

  if (regionalRates.length) {
    return (
      <div className="shipping-rate-card__regional__rate-group">
        {regionalRates.map((rr, i) => (
          <RegionalRateGroupActive
            regionalRate={rr}
            rateType={rateType}
            handleDeleteRateGroup={handleDeleteRateGroup}
            handleEditRateGroup={handleEditRateGroup}
            handleModalOpen={handleModalOpen}
            countryCode={countryCode}
            showCreateButton={allowCreateButton && !isFree && i === regionalRates.length - 1}
            freeOrPaid={freeOrPaid}
            key={rr.rate.amount}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="shipping-rate-card__regional__rate-group shipping-rate-card__regional__rate-group--new">
      <span>
        {I18n.t(`discovery.dashboard.selling.shippingRates.shippingModeRadios.regional.options.${freeOrPaid}.new`, {
          rateType: I18n.t(`discovery.dashboard.selling.shippingRates.shippingModeRadios.regional.options.rateType.${rateType}`).toLowerCase(),
        })}
      </span>
      <RCButton
        id={isFree ? `add-free-${rateType}-regional-rate-group-button` : `add-${rateType}-regional-rate-group-button`}
        onClick={handleModalOpen}
      >
        {I18n.t(`discovery.dashboard.selling.shippingRates.shippingModeRadios.regional.options.${freeOrPaid}.button`)}
      </RCButton>
    </div>
  );
}

export default RegionalRateGroup;
